import { useEffect, useRef, useCallback } from 'react';
import { getCustomerAuthToken } from '../../components/settings/components/billing-subscription/helpers/billing-subscription.api';
import hasPermission from '../utils/access-control/has-permission';
import { Permissions } from '../utils/access-control/enums/permissions';

const EXPIRY_BUFFER_MS = 2 * 60 * 1000; // 2 minutes before actual expiry

interface CachedToken {
  customerAuthToken: string;
  expiresAt: number; // timestamp in ms
}

const usePaddleCustomerAuthToken = (customerId: string | undefined) => {
  const cacheRef = useRef<CachedToken | null>(null);
  const fetchingRef = useRef<Promise<string | null> | null>(null);
  const mountedRef = useRef(true);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      cacheRef.current = null;
      fetchingRef.current = null;
    };
  }, []);

  // Clear cache when customerId changes
  useEffect(() => {
    cacheRef.current = null;
    fetchingRef.current = null;
  }, [customerId]);

  const fetchToken = useCallback(async (): Promise<string | null> => {
    if (!customerId) return null;
    if (!hasPermission(Permissions.ACCOUNT_SUBSCRIPTION_READ)) return null;

    try {
      const response = await getCustomerAuthToken(customerId);
      const data = (response as any)?.payload;

      if (!data?.customerAuthToken || !data?.expiresAt) return null;
      if (!mountedRef.current) return null;

      const cached: CachedToken = {
        customerAuthToken: data.customerAuthToken,
        expiresAt: new Date(data.expiresAt).getTime(),
      };
      cacheRef.current = cached;
      return cached.customerAuthToken;
    } catch {
      return null;
    }
  }, [customerId]);

  // Eager fetch on mount / customerId change
  useEffect(() => {
    if (customerId) {
      fetchToken();
    }
  }, [customerId, fetchToken]);

  const getToken = useCallback(async (): Promise<string | null> => {
    if (!customerId) return null;

    // Return cached token if still valid (with buffer)
    if (
      cacheRef.current &&
      Date.now() < cacheRef.current.expiresAt - EXPIRY_BUFFER_MS
    ) {
      return cacheRef.current.customerAuthToken;
    }

    // Deduplicate concurrent fetches
    if (fetchingRef.current) {
      return fetchingRef.current;
    }

    const promise = fetchToken();
    fetchingRef.current = promise;

    try {
      return await promise;
    } finally {
      fetchingRef.current = null;
    }
  }, [customerId, fetchToken]);

  return { getToken };
};

export default usePaddleCustomerAuthToken;
